import React, { useEffect } from 'react';
import { usePeprisGame } from './PeprisCore';
import InstructionsDialog from './InstructionsDialog';
import { BASE_URL, PEPPER_SHAPES, PEPPER_COLORS } from './constants';

const PeprisDesktop = () => {
  const {
    gameState,
    playerInfo,
    setPlayerInfo,
    isSoundOn,
    audioRefs,
    moveDown,
    moveHorizontally,
    rotatePepper,
    changeCurrentPepperColor,
    startGame,
    resetGame,
    toggleSound,
    saveHighScore,
    highScores
  } = usePeprisGame();
  
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (gameState.gameOver) return;

      if (event.altKey || event.key === '0') {
        event.preventDefault();
        changeCurrentPepperColor();
      }

      switch (event.key) {
        case 'ArrowLeft':
          moveHorizontally(-1);
          break;
        case 'ArrowRight':
          moveHorizontally(1);
          break;
        case 'ArrowDown':
          moveDown();
          break;
        case 'ArrowUp':
          rotatePepper();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState.gameOver, moveDown, moveHorizontally, rotatePepper, changeCurrentPepperColor]);

  const renderCell = React.useCallback((cellValue, rowIndex, colIndex) => {
    let cellClass = 'w-6 h-6 border border-gray-700 relative ';

    if (cellValue && cellValue !== 'white') {
      cellClass += cellValue;
    } else {
      cellClass += 'bg-gray-900';
    }

    if (gameState.ghostPepper && 
        rowIndex >= gameState.ghostPepper.y && 
        rowIndex < gameState.ghostPepper.y + gameState.ghostPepper.shape.length &&
        colIndex >= gameState.ghostPepper.x && 
        colIndex < gameState.ghostPepper.x + gameState.ghostPepper.shape[0].length &&
        gameState.ghostPepper.shape[rowIndex - gameState.ghostPepper.y][colIndex - gameState.ghostPepper.x]) {
      cellClass += ' border-2 border-white border-opacity-50';
    }

    if (gameState.currentPepper && 
        rowIndex >= gameState.currentPepper.y && 
        rowIndex < gameState.currentPepper.y + gameState.currentPepper.shape.length &&
        colIndex >= gameState.currentPepper.x && 
        colIndex < gameState.currentPepper.x + gameState.currentPepper.shape[0].length &&
        gameState.currentPepper.shape[rowIndex - gameState.currentPepper.y][colIndex - gameState.currentPepper.x]) {
      cellClass = `w-6 h-6 border border-gray-700 ${gameState.currentPepper.color} z-10`;
    }

    if (gameState.clearedLines.includes(rowIndex)) {
      cellClass += ' transition-colors duration-50';
    }

    return (
      <div 
        key={`${rowIndex}-${colIndex}`} 
        className={cellClass}
        style={{ isolation: 'isolate' }}
      />
    );
  }, [gameState.ghostPepper, gameState.currentPepper, gameState.clearedLines]);

  const renderPepperPreview = React.useCallback((pepper, title) => {
    if (!pepper) return null;
    const maxSize = Math.max(pepper.shape.length, pepper.shape[0].length);
    return (
      <div className="border-2 border-gray-300 p-2 mt-4">
        <h3 className="text-lg font-bold mb-2">{title}:</h3>
        <div className="w-24 h-24 flex items-center justify-center">
          <div 
            className="grid gap-0"
            style={{ 
              gridTemplateColumns: `repeat(${pepper.shape[0].length}, 1fr)`,
              transform: `scale(${4 / maxSize})`
            }}
          >
            {pepper.shape.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <div
                  key={`${title}-${rowIndex}-${colIndex}`}
                  className={`w-6 h-6 ${cell ? pepper.color : 'bg-transparent'}`}
                />
              ))
            )}
          </div>
        </div>
      </div>
    );
  }, []);

  const renderPepperShapes = React.useCallback(() => (
    <div className="ml-4 max-w-xs lg:max-w-full">
      <h3 className="text-xl font-bold mb-2">Pepper Bonus Shapes:</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(PEPPER_SHAPES).map(([pepperType, shape]) => (
          <div key={pepperType} className="mb-4">
            <p className="font-semibold">{pepperType}</p>
            <p className="text-sm mb-1">Bonus: {100 * shape.flat().filter(Boolean).length * 2} pts</p>
            <div className="grid grid-cols-5 gap-x-0 gap-y-1 border border-gray-300 p-1 bg-white w-max">
              {shape.map((row, rowIndex) => (
                row.map((cell, colIndex) => (
                  <div
                    key={`${pepperType}-${rowIndex}-${colIndex}`}
                    className={`w-4 h-4 ${cell ? PEPPER_COLORS[pepperType] : 'bg-gray-200'}`}
                  />
                ))
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  ), []);

  const renderHighScores = React.useCallback(() => (
    <div className="mt-4">
      <h3 className="text-xl font-bold mb-2">High Scores</h3>
      <ul className="space-y-1">
        {highScores.map((highScore, index) => (
          <li key={index} className="text-lg">
            {highScore.name}: {highScore.score}
          </li>
        ))}
      </ul>
    </div>
  ), [highScores]);

  const renderGameControls = React.useCallback(() => (
    <div className="mt-4 grid grid-cols-5 gap-2 max-w-md mx-auto">
      <button
        onClick={() => moveHorizontally(-1)}
        aria-label="Move left"
        className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 active:bg-blue-700 flex items-center justify-center"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 6 9 12 15 18" />
        </svg>
      </button>
      <button
        onClick={moveDown}
        aria-label="Move down"
        className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 active:bg-blue-700 flex items-center justify-center"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <button
        onClick={() => moveHorizontally(1)}
        aria-label="Move right"
        className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 active:bg-blue-700 flex items-center justify-center"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
      <button
        onClick={rotatePepper}
        aria-label="Rotate"
        className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 active:bg-blue-700 flex items-center justify-center"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <polyline points="21 3 21 7 17 7" />
        </svg>
      </button>
      <button
        onClick={changeCurrentPepperColor}
        className="bg-purple-500 text-white p-2 rounded hover:bg-purple-600 active:bg-purple-700"
      >
        Change Color (Alt)
      </button>
    </div>
  ), [moveHorizontally, moveDown, rotatePepper, changeCurrentPepperColor]);

  const renderGameOver = React.useCallback(() => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full">
        <h2 className="text-2xl font-bold mb-4">Game Over!</h2>
        <p className="mb-4 text-xl">Your final score: {gameState.score}</p>
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Your Name"
            value={playerInfo.name}
            onChange={(e) => setPlayerInfo(prev => ({ ...prev, name: e.target.value }))}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="email"
            placeholder="Your Email"
            value={playerInfo.email}
            onChange={(e) => setPlayerInfo(prev => ({ ...prev, email: e.target.value }))}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="tel"
            placeholder="Your Phone"
            value={playerInfo.phone}
            onChange={(e) => setPlayerInfo(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={saveHighScore}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Save Score
          </button>
          <button
            onClick={resetGame}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Play Again
          </button>
        </div>
      </div>
    </div>
  ), [gameState.score, playerInfo, setPlayerInfo, saveHighScore, resetGame]);

  return (
    <div className="hidden md:flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-red-100 to-yellow-100 p-4">
      <h1 className="text-4xl font-bold mb-4 text-red-600">Pepris</h1>
      
      <div className="flex flex-col md:flex-row justify-between w-full max-w-4xl mb-4">
        <div className="text-xl font-semibold">Score: {gameState.score}</div>
        <div className="text-xl font-semibold">Level: {gameState.level}</div>
        <button onClick={toggleSound} className="bg-gray-300 px-2 py-1 rounded hover:bg-gray-400">
          {isSoundOn ? "Sound Off" : "Sound On"}
        </button>
      </div>

      <div className="flex flex-col md:flex-row items-start">
        <div className="mr-4 mb-4 md:mb-0">
          {renderPepperPreview(gameState.nextPepper, "Next Pepper")}
          <div className="mt-4">
            <h3 className="text-xl font-bold mb-2">Active Power-up:</h3>
            <p>{gameState.activePowerUp || 'None'}</p>
            <p>Cooldown: {gameState.powerUpCooldown}s</p>
          </div>
        </div>

        <div className="border-4 border-red-600 p-1 bg-black rounded-lg shadow-lg">
          {gameState.board.map((row, rowIndex) => (
            <div key={rowIndex} className="flex">
              {row.map((cell, colIndex) => renderCell(cell, rowIndex, colIndex))}
            </div>
          ))}
        </div>

        {renderPepperShapes()}
      </div>

      {renderGameControls()}

      <audio ref={audioRefs.background} src={`${BASE_URL}/sounds/background.mp3`} loop />
      <audio ref={audioRefs.row} src={`${BASE_URL}/sounds/row.m4a`} />
      <audio ref={audioRefs.buzz} src={`${BASE_URL}/sounds/buzz.m4a`} />
      <audio ref={audioRefs.clock} src={`${BASE_URL}/sounds/clock.mp3`} />
      <audio ref={audioRefs.bonus} src={`${BASE_URL}/sounds/bonus.mp3`} />

      <div className="mt-4 text-center text-sm">
        <p>
          Dit spel is je aangeboden door{' '}
          <a
            data-tracking="chili-link"
            href="https://www.dutchchilifest.nl/"
            className="text-blue-600 hover:text-blue-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            Dutch Chili Fest
          </a>
          {' '}en{' '}
          <a
            data-tracking="Pepris-Theuws-link"
            href="https://theuws.com/internet/haal-het-meeste-uit-je-onderneming-met-ai/"
            className="text-blue-600 hover:text-blue-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            Theuws.com
          </a>
        </p>
      </div>

      {gameState.gameOver && renderGameOver()}
      
      <InstructionsDialog
        isOpen={gameState.showInstructions}
        onClose={() => {
          startGame();
        }}
      />

      {renderHighScores()}
    </div>
  );
};

export default PeprisDesktop;